import { Link } from '@tanstack/react-router';
import { FileUp } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';

export interface FileSelectorProps {
  file: File | null;
  setFile: (file: File | null) => void;
  onNext: () => void;
}

export function FileSelector({ file, setFile, onNext }: FileSelectorProps) {
  const [showAlert, setShowAlert] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: Array<File>) => {
      const file = acceptedFiles[0];
      if (file) setFile(file);
    },
    [setFile],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
    },
    multiple: false,
  });

  const handleNextClick = () => {
    setShowAlert(true);
  };

  const handleConfirm = () => {
    setShowAlert(false);
    onNext();
  };

  return (
    <Card className='mx-auto w-full max-w-4xl'>
      <CardContent className='p-6'>
        <h1 className='mb-6 text-2xl font-semibold'>
          Bulk Upload Parents & Students
        </h1>

        <div className='space-y-6'>
          <ol className='list-inside list-decimal space-y-2'>
            <li>Download the template file (last updated on 01/25/2025).</li>
            <li>
              Fill in all required parent details in the downloaded file. Ensure
              each field aligns with the format and naming in the template.
            </li>
            <li>Save the file as .csv format.</li>
            <li>Upload the file below.</li>
          </ol>

          <div className='space-y-4 rounded-lg border p-4'>
            <h2 className='font-semibold'>Template Instructions</h2>
            <ol className='list-inside list-decimal space-y-2'>
              <li>Do not add or remove columns from the template.</li>
              <li>
                Leave fields blank if no data is available for that cell. Do not
                use &quot;-&quot;, &quot;NA,&quot; or similar placeholders.
              </li>
            </ol>
          </div>

          <div className='space-y-6'>
            <div
              {...getRootProps()}
              className={`cursor-pointer rounded-lg border-2 border-dashed border-primary p-6 text-center transition-colors ${
                isDragActive ? 'bg-accent' : ''
              }`}
            >
              <input {...getInputProps()} />
              <FileUp className='mx-auto mb-4 size-12 text-primary' />
              {file ? (
                <p className='font-semibold'>File selected: {file.name}</p>
              ) : (
                <p>Drag & drop your file here, or click to select a file</p>
              )}
              <p className='mt-2 text-sm'>Accepted file type: .csv</p>
            </div>
            <div className='text-sm'>
              <Button asChild variant={'link'} className='px-0'>
                <a
                  href='https://storage.googleapis.com/enrolliocsv-example/Parent%20Upload%20template%20(4).csv'
                  download='class-csv-example.csv'
                >
                  Download
                </a>
              </Button>{' '}
              sample CSV
            </div>
            <div>
              <p>
                Need help! Chat in with live chat support or email support at{' '}
                <a href='mailto:support@enrollio.ai' className='underline'>
                  support@enrollio.ai
                </a>
                .
              </p>
            </div>
            <div className='flex flex-col'>
              <Button onClick={handleNextClick} disabled={!file}>
                Go To Mapping
              </Button>
            </div>
          </div>
        </div>
      </CardContent>

      <AlertDialog open={showAlert} onOpenChange={setShowAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Important Notice</AlertDialogTitle>
            <AlertDialogDescription>
              Uploading this CSV creates parents and students and stages an
              onboarding email batch. No welcome emails are sent automatically.
              When you are ready, release the batch from Parent Onboarding Email
              Batch so families get their login link and one-time password.
            </AlertDialogDescription>
            <div className='space-y-2 text-sm text-muted-foreground'>
              <div>
                <Button asChild variant='link' className='h-auto px-0'>
                  <Link to='/email-customizer'>Set up email template</Link>
                </Button>
              </div>
              <div>
                <span className='font-semibold text-foreground'>Note: </span>
                Mass onboarding email stays manual. Release the batch only when
                you want families notified.
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} className='w-auto'>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
